import { Stack, Text } from '@ltvco/refresh-lib/theme';
import { Record } from '@ltvco/refresh-lib/v1';

import { PersonParamsFunctionArguments } from 'components/ChatBV/types';
import { formatShorthandDate } from 'routes/SearchPerson/Cards/NewCardsDesignVariation1/common/utils';

import { PersonReportSummaryData } from '../DataTypes/SummaryTypes';

import { BoldSpan } from './components';

interface PersonTeaserRendererProps {
  data: Record[] | undefined;
  params: PersonParamsFunctionArguments | undefined;
}

interface PersonReportRendererProps {
  data: PersonReportSummaryData;
}

export const PersonTeaserRenderer = ({
  data,
  params,
}: PersonTeaserRendererProps) => {
  const hasLocation = params?.city || params?.state;
  const hasAge = params?.age;
  return (
    <Stack spacing={3} py={2}>
      <Text>
        We found {data?.length || 0} result(s) for individuals with the name{' '}
        <BoldSpan>
          {params?.first_name || ''}{' '}
          {params?.middle_name ? `${params?.middle_name} ` : ''}
          {params?.last_name || ''}
        </BoldSpan>
        {hasLocation ? ' located in ' : ''}
        {hasLocation ? (
          <BoldSpan>
            {params?.city || ''}
            {params?.state ? `, ${params?.state}` : ''}
          </BoldSpan>
        ) : (
          ''
        )}
        {hasAge ? ' aged ' : ''}
        {hasAge ? <BoldSpan>{params?.age || ''}</BoldSpan> : ''}
      </Text>
      <Text>
        Try adding more filters to narrow down the possible results or click on
        one of the results below to see more information about that person.
      </Text>
      <Text>Showing results in the United States only.</Text>
    </Stack>
  );
};

export const PersonReportRenderer = ({ data }: PersonReportRendererProps) => {
  const hasLocation = data?.location;
  return (
    <Stack spacing={3} py={2}>
      <Text>
        Here are the key details for <BoldSpan>{data.personName}</BoldSpan>
        {hasLocation ? ' located in ' : ''}
        {hasLocation ? <BoldSpan>{data.location}</BoldSpan> : ''}
      </Text>
      <Stack spacing={1.5}>
        <Text>
          <BoldSpan>Summary</BoldSpan>
        </Text>
        <Text>
          <BoldSpan>Age:</BoldSpan> {data.age || '-'}
        </Text>
        <Text>
          <BoldSpan>Born:</BoldSpan>{' '}
          {typeof data?.dateOfBirth === 'string'
            ? data?.dateOfBirth
            : formatShorthandDate(data?.dateOfBirth) || '-'}
        </Text>
        <Text>
          <BoldSpan>Aliases:</BoldSpan> {data.aliases?.join(', ') || '-'}
        </Text>
      </Stack>
      <Stack spacing={1.5}>
        <Text>
          <BoldSpan>Contact Info</BoldSpan>
        </Text>
        <Text>📞 {data.phone || '-'}</Text>
        <Text>✉️ {data.email || '-'}</Text>
        <Text>🏠 {data.address || '-'}</Text>
      </Stack>
      <Stack spacing={1.5}>
        <Text>
          <BoldSpan>Work & Education</BoldSpan>
        </Text>
        {data.jobs && <Text>{data.jobs}</Text>}
        {data.education && <Text>{data.education}</Text>}
        {!data.jobs && !data.education && <Text>-</Text>}
      </Stack>
      <Stack spacing={1.5}>
        <Text>
          <BoldSpan>Relatives</BoldSpan>
        </Text>
        {data.relatives?.length > 0 ? (
          data.relatives.map((relative) => (
            <Text key={relative}>• {relative}</Text>
          ))
        ) : (
          <Text>-</Text>
        )}
      </Stack>
    </Stack>
  );
};
